/**
 * "Pick the QuickBooks customer" (#qbo-customer-pick, 10/28).
 *
 * When QuickBooks has more than one ACTIVE customer with the same name, a push
 * refuses to guess which one the invoice belongs to. The server says so with a
 * 409 `QBO_CUSTOMER_PICK` (manual push) or, for a queued push, a stored error
 * that starts with a fixed phrase. Either way the office gets the chooser.
 *
 * The phrases must match quickbooksService.ts (QBO_CUSTOMER_PICK_PHRASE /
 * QBO_CUSTOMER_GONE_PHRASE).
 */
import type { InfoTopic } from "@/components/info-button";

export const QBO_CUSTOMER_PICK_PHRASE = "QuickBooks has more than one customer named";
export const QBO_CUSTOMER_GONE_PHRASE = "The QuickBooks customer this one was linked to is no longer active";

export interface QboCustomerCandidate {
    id: string;
    name: string;
    email: string | null;
    balance: number | null;
}

export interface QboCustomerChoices {
    customer: { id: string; name: string };
    linked: QboCustomerCandidate | null;
    candidates: QboCustomerCandidate[];
}

/** Does this push error mean "a person has to pick the QuickBooks customer"? */
export function needsQboCustomerPick(message: string | null | undefined): boolean {
    const m = message ?? "";
    // Older rows say it the old way ("Ambiguous QBO customer name …").
    return (
        m.includes(QBO_CUSTOMER_PICK_PHRASE) ||
        m.includes(QBO_CUSTOMER_GONE_PHRASE) ||
        /Ambiguous QBO customer name/i.test(m)
    );
}

/** Same check for an API error body from a manual push. */
export function isQboCustomerPickBody(body: unknown): body is { error: "QBO_CUSTOMER_PICK"; customerId: string } {
    return !!body && typeof body === "object" && (body as { error?: unknown }).error === "QBO_CUSTOMER_PICK";
}

export const QBO_CUSTOMER_PICK_HELP: InfoTopic = {
    title: "Why do I have to pick?",
    lines: [
        "QuickBooks has two or more customers with this exact name.",
        "We won't guess, because the wrong one puts the money on the wrong account.",
        "Tap the right one. We save it, so this customer never asks again.",
        "Fixed the duplicate in QuickBooks instead? Tap Try again — we check QuickBooks again right now.",
    ],
};
